import { Link } from "react-router-dom";
import { useCart } from "../context/AppContext";
import { toast } from "sonner";
import { Youtube, Truck } from "lucide-react";
import { normaliseImageUrl, handleImageError } from "../lib/driveImage";
import { isSoldOut, normalizeProduct, primaryImage } from "../lib/productHelpers";

export default function ProductCard({ product, index = 0 }) {
  const norm = normalizeProduct(product);
  const { add } = useCart();
  const out = isSoldOut(norm);
  const hasVariants = (norm.variants?.length || 0) > 1;

  const cardImage = primaryImage(norm) || "https://images.pexels.com/photos/21326994/pexels-photo-21326994.jpeg";

  const currentPrice = norm.price;
  const currentMrp = norm.mrp;
  const hasDiscount = currentMrp > currentPrice;
  const discountPct = hasDiscount ? Math.round(((currentMrp - currentPrice) / currentMrp) * 100) : 0;
  const shippingCharge = Number(norm.shippingCharge || 0);

  return (
    <div
      data-testid={`product-card-${norm.id}`}
      className="group relative bg-card border border-border overflow-hidden animate-fade-up flex flex-col justify-between"
      style={{ animationDelay: `${index * 60}ms` }}
    >
      <div>
        <Link to={`/product/${norm.id}`} className="block relative aspect-[4/5] overflow-hidden bg-muted">
          <img
            src={normaliseImageUrl(cardImage, 600)}
            alt={norm.name}
            className="w-full h-full object-cover transition-all duration-700 group-hover:scale-105"
            loading="lazy"
            decoding="async"
            referrerPolicy="no-referrer"
            onError={(e) => handleImageError(e, cardImage, 600)}
          />
          <span className="absolute top-3 left-3 overline bg-background/90 px-2 py-1 rounded-sm">{norm.category}</span>

          
          {out && (
            <span data-testid={`sold-out-${norm.id}`} className="absolute top-3 right-3 text-xs bg-destructive text-destructive-foreground px-2 py-1 rounded-sm font-medium tracking-wide">
              SOLD OUT
            </span>
          )}

          {norm.videoUrl && (
            <span data-testid={`has-video-${norm.id}`} className="absolute bottom-3 right-3 inline-flex items-center gap-1 text-xs bg-background/95 px-2 py-1 rounded-sm z-10">
              <Youtube className="w-3 h-3 text-[#FF0000]" /> Video
            </span>
          )}
        </Link>

        <div className="p-3 sm:p-5">
          <div className="flex flex-wrap items-center gap-1 sm:gap-2 mb-1.5 sm:mb-2">
            {shippingCharge === 0 ? (
              <span className="inline-flex items-center gap-1 text-[10px] sm:text-[11px] font-semibold text-emerald-800 bg-emerald-50 border border-emerald-200 px-1.5 sm:px-2 py-0.5 rounded-full">
                <Truck className="w-2.5 h-2.5 sm:w-3 sm:h-3" /> Free Shipping
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-[10px] sm:text-[11px] font-medium text-amber-800 bg-amber-50 border border-amber-200 px-1.5 sm:px-2 py-0.5 rounded-full">
                <Truck className="w-2.5 h-2.5 sm:w-3 sm:h-3" /> +<span className="font-price font-semibold">₹{shippingCharge}</span>
              </span>
            )}
            {hasVariants && (
              <span className="text-[10px] sm:text-[11px] text-muted-foreground bg-muted px-1.5 sm:px-2 py-0.5 rounded-full">
                {norm.variants.length} colors
              </span>
            )}
          </div>

          <Link to={`/product/${norm.id}`} className="block">
            <h3 className="font-serif text-sm sm:text-xl font-semibold leading-snug sm:leading-tight mb-1 hover:text-terracotta transition-colors line-clamp-2">{norm.name}</h3>
          </Link>
          <p className="text-xs sm:text-sm text-muted-foreground line-clamp-1 sm:line-clamp-2 mb-2 sm:mb-4">{norm.description}</p>
        </div>
      </div>

      <div className="p-3 sm:p-5 pt-0 sm:pt-0">
        <div className="flex items-baseline gap-1.5 sm:gap-2 mb-2 sm:mb-3 flex-wrap">
          <span className="font-price text-base sm:text-2xl font-bold tracking-tight text-foreground">₹{currentPrice}</span>
          {hasDiscount && (
            <>
              <span className="font-price text-xs sm:text-sm text-muted-foreground line-through">₹{currentMrp}</span>
              <span className="font-price text-[10px] sm:text-xs font-semibold text-emerald-700 bg-emerald-50 px-1 sm:px-1.5 py-0.5 rounded">
                {discountPct}% OFF
              </span>
            </>
          )}
        </div>

        <div className="flex items-center justify-between gap-2">
          {hasVariants ? (
            <Link
              to={`/product/${norm.id}`}
              data-testid={`choose-variant-${norm.id}`}
              className={`w-full text-center text-xs sm:text-sm px-2.5 sm:px-4 py-1.5 sm:py-2.5 rounded-full font-medium transition-colors ${
                out
                  ? "bg-muted text-muted-foreground border border-border hover:bg-foreground hover:text-background"
                  : "bg-foreground text-background hover:bg-terracotta"
              }`}
            >
              {out ? "View details (Sold out)" : "Choose variant"}
            </Link>
          ) : (
            <button
              data-testid={`add-to-cart-${norm.id}`}
              disabled={out}
              aria-disabled={out}
              onClick={() => {
                if (out) return;
                add(norm, norm.variants?.[0]);
                toast.success(`${norm.name} added to cart`);
              }}
              className="w-full text-xs sm:text-sm px-2.5 sm:px-4 py-1.5 sm:py-2.5 rounded-full bg-foreground text-background hover:bg-terracotta transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:pointer-events-none font-medium"
            >
              {out ? "Sold out" : "Add to cart"}
            </button>
          )}
        </div>
        <div className="mt-1.5 sm:mt-2 text-[10px] sm:text-xs text-muted-foreground text-center">
          {out ? "Out of stock" : `In stock: ${norm.stock}`}
        </div>
      </div>
    </div>
  );
}
